import React from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * ExperimentConfigurationSection Component
 *
 * Displays experiment configuration: traffic allocation, sample size, significance, and success metrics.
 *
 * @param {Object} props
 * @param {Object} props.experiment - Experiment object
 * @param {Function} props.formatTrafficAllocation - Function to format traffic allocation
 */
function ExperimentConfigurationSection({
  experiment,
  formatTrafficAllocation,
}) {
  const theme = useTheme();

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Divider
          sx={{
            my: 0.75,
            borderColor: theme.palette.divider,
            borderWidth: '1px',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 0.75,
            letterSpacing: '-0.01em',
          }}
        >
          Configuration
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '10px',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CardContent sx={{ p: 1.5 }}>
            {/* Traffic Allocation */}
            <Box
              sx={{
                mb: 1,
                pb: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Traffic Allocation
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                }}
              >
                {formatTrafficAllocation(experiment.traffic_allocation)}
              </Typography>
            </Box>

            {/* Minimum Sample Size and Statistical Significance */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: 1.5,
                mb: 1,
                pb: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* Minimum Sample Size */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Minimum Sample Size
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                >
                  {experiment.minimum_sample_size || 'Not specified'}
                </Typography>
              </Box>

              {/* Statistical Significance */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Statistical Significance
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                >
                  {experiment.statistical_significance
                    ? `${(experiment.statistical_significance * 100).toFixed(1)}%`
                    : 'Not specified'}
                </Typography>
              </Box>
            </Box>

            {/* Success Metrics */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Success Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {experiment.success_metrics?.length > 0 ? (
                  experiment.success_metrics.map((metric, index) => (
                    <Tooltip
                      key={index}
                      title={metric}
                      arrow
                      enterDelay={300}
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '1.25rem',
                          },
                        },
                      }}
                    >
                      <Chip
                        label={metric}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.info.light,
                          color: theme.palette.info.dark,
                          border: `1px solid ${theme.palette.info.main}`,
                          height: '28px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          maxWidth: '200px',
                          '& .MuiChip-label': {
                            padding: '0 12px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    </Tooltip>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.875rem',
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                    }}
                  >
                    No metrics defined
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}

export default ExperimentConfigurationSection;
